import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { DeleteOrderComponent } from '@components/orders/_common/delete-order/delete-order.component';
import { OrderExportComponent } from '@components/orders/_common/order-export/order-export.component';
import { ArchiveOrderDialogComponent } from '@components/orders/manage-orders/archive-order-dialog/archive-order-dialog.component';
import { OrderChangeHistoryComponent } from '@components/orders/manage-orders/order-change-history/order-change-history.component';
import { OrderItemProcessesComponent } from '@components/orders/manage-orders/order-item-processes/order-item-processes.component';
import { OrderJobDetailsComponent } from '@components/orders/manage-orders/order-job-details/order-job-details.component';
import { OrderJobFollowUpComponent } from '@components/orders/manage-orders/order-job-follow-up/order-job-follow-up.component';
import { OrderManageGeneralComponent } from '@components/orders/manage-orders/order-manage-general/order-manage-general.component';
import { OrderNoteHistoryComponent } from '@components/orders/manage-orders/order-note-history/order-note-history.component';
import { ProductionFlowComponent } from '@components/orders/manage-orders/production-flow/production-flow.component';
import { Subscription } from 'rxjs';

import { OrdersActions } from '../../../../../@store/orders/orders.actions';
import { singleOrderDetails } from '../../../../../@store/orders/orders.model';
import {
  selectOrderDetails,
  selectOrderError,
  selectOrderLoading,
} from '../../../../../@store/orders/orders.selectors';
import { CONFIG } from '../../../../../config';
import { SafeAny } from '../../../../../types/safe-any';

@Component({
  selector: 'ciwc-view-orders',
  imports: [
    OrderManageGeneralComponent,
    ReactiveFormsModule,
    MatCardModule,
    DatePipe,
    OrderJobDetailsComponent,
    MatProgressSpinnerModule,
    ProductionFlowComponent,
    OrderItemProcessesComponent,
    OrderJobFollowUpComponent,
    OrderNoteHistoryComponent,
    OrderChangeHistoryComponent,
    MatIcon,
    RouterLink,
    MatDialogModule,
  ],
  templateUrl: './view-orders.component.html',
  styleUrl: './view-orders.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewOrdersComponent implements OnInit {
  @ViewChild(OrderItemProcessesComponent) itemProcessesComponent!: OrderItemProcessesComponent;

  readonly selectedOrder = this.store.selectSignal(selectOrderDetails);
  readonly selectedOrderLoading = this.store.selectSignal(selectOrderLoading);
  readonly selectedOrderError = this.store.selectSignal(selectOrderError);

  form: FormGroup;
  generalForm: FormGroup;
  order: SafeAny = null;
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;

  boundCreateShipmentGroup: () => FormGroup;
  private orderId: string | null = null;
  private saveSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    protected route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
  ) {
    effect(() => {
      this.order = this.selectedOrder();

      if (this.order) {
        this.patchOrderForm(this.order);
      }
    });
    this.generalForm = this.fb.group({
      assigneeId: [{ value: null, disabled: true }],
      status: [{ value: null, disabled: true }],
      description: [{ value: '', disabled: true }],
      clientNotes: [{ value: '', disabled: true }],
      client: [{ value: null, disabled: true }],
      clientsId: [{ value: null, disabled: true }],
    });
    this.form = this.fb.group({
      general: this.generalForm,
      caCustomDescription: [{ value: '', disabled: true }],
      clientPartNo: [{ value: '', disabled: true }],
      feetPerReel: [{ value: '', disabled: true }],
      jobDetails: this.fb.group({
        clientPurchaseOrder: [{ value: '', disabled: true }],
        pricePerUnit: [{ value: null, disabled: true }],
        orderTotalPrice: [{ value: null, disabled: true }],
        totalQuantity: [{ value: null, disabled: true }],
        balanceToFollow: [{ value: null, disabled: true }],
        overage: [{ value: null, disabled: true }],
        weeksAmount: [{ value: null, disabled: true }],
        promisedDate: [{ value: null, disabled: true }],
        expeditedOrder: [{ value: false, disabled: true }],
        expediteDate: [{ value: null, disabled: true }],
        expeditePrice: [{ value: null, disabled: true }],
        faiReport: [{ value: false, disabled: true }],
        faiReportPrice: [{ value: null, disabled: true }],
        lastShipmentAmount: [{ value: null, disabled: true }],
        testReports: [{ value: false, disabled: true }],
        certsRequired: [{ value: false, disabled: true }],
        cfm: [{ value: false, disabled: true }],
        ciwToSupplyWire: [{ value: false, disabled: true }],
        isComplete: [{ value: false, disabled: true }],
        shippingDetailsText: [{ value: '', disabled: true }],
        followUps: this.fb.array([this.createFollowUpGroup()]),
      }),
      shippingDetails: this.fb.group({
        address1Street: [{ value: '', disabled: true }],
        address1StreetDetails: [{ value: '', disabled: true }],
        address1City: [{ value: '', disabled: true }],
        address1Zip: [{ value: '', disabled: true }],
        address1Country: [{ value: '', disabled: true }],
        address1State: [{ value: '', disabled: true }],
        attn: [{ value: '', disabled: true }],
        shippingNotes: [{ value: '', disabled: true }],
      }),
      shipments: this.fb.array([]),
      materials: this.fb.array([]),
      materialsReceived: [{ value: false, disabled: true }],
      specification: this.fb.group({
        awg: [[]],
        stranding: [[]],
        wireType: [[]],
        supplier: [[]],
        finishedDiameter: [[]],
        tip: [[]],
        die: [[]],
        putUp: [[]],
        jacketMaterial: [[]],
        jacketColor: [[]],
        jacketPrinting: [[]],
        wallThickness: [[]],
        innerDiameter: [[]],
        outerDiameter: [[]],
      }),
    });
    this.boundCreateShipmentGroup = this.createShipmentGroup.bind(this);
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      if (params['id']) {
        this.orderId = params['id'];
        this.store.dispatch(OrdersActions.loadOrder({ id: params['id'].toString() }));
      }
    });
  }

  patchOrderForm(order: singleOrderDetails) {
    // Patch caCustomDescription
    this.form.get('caCustomDescription')?.setValue(order.caCustomDescription || '');
    this.form.get('clientPartNo')?.setValue(order.details[0]?.clientPartNo || '');
    this.form.get('feetPerReel')?.setValue(order.details[0]?.feetPerReel || '');

    // Patch specification data from order.details[0] for checkboxes to show
    if (order.details && order.details[0]) {
      const details = order.details[0];
      const specGroup = this.form.get('specification') as FormGroup;

      // Define the specification fields that need to be populated
      const specFields = [
        'awg',
        'stranding',
        'wireType',
        'supplier',
        'finishedDiameter',
        'tip',
        'die',
        'putUp',
        'jacketMaterial',
        'jacketColor',
        'jacketPrinting',
        'wallThickness',
        'innerDiameter',
        'outerDiameter',
      ];

      specFields.forEach((field) => {
        const fieldValue = details[field as keyof typeof details];
        if (fieldValue && fieldValue !== '' && fieldValue !== null) {
          const values = Array.isArray(fieldValue)
            ? fieldValue.filter((v) => v && v !== '' && v !== null)
            : [fieldValue];
          // Only populate with actual values after filtering
          if (values.length > 0) {
            specGroup.patchValue({ [field]: values });
          } else {
            // Set placeholder value so checkbox is visible but unchecked
            specGroup.patchValue({ [field]: [''] });
          }
        } else {
          // Set placeholder value so checkbox is visible but unchecked
          specGroup.patchValue({ [field]: [''] });
        }
      });
    }

    // Patch followUps
    const followUpsArray = this.followUps;
    followUpsArray.clear();
    if (order.followUps && order.followUps.length) {
      order.followUps.forEach((fu) => {
        followUpsArray.push(
          this.fb.group({
            dateFollowUp: [
              { value: fu.dateFollowUp ? new Date(fu.dateFollowUp) : null, disabled: true },
            ],
            priorFirstDate: [
              { value: fu.priorFirstDate ? new Date(fu.priorFirstDate) : null, disabled: true },
            ],
            priorSecondDate: [
              { value: fu.priorSecondDate ? new Date(fu.priorSecondDate) : null, disabled: true },
            ],
            priorThirdDate: [
              { value: fu.priorThirdDate ? new Date(fu.priorThirdDate) : null, disabled: true },
            ],
            notes: [{ value: fu.notes, disabled: true }],
            ciw: [{ value: !!fu.ciw, disabled: true }],
            cfm: [{ value: !!fu.cfm, disabled: true }],
          }),
        );
      });
    } else {
      followUpsArray.push(this.createFollowUpGroup());
    }
  }

  get jobDetailsForm(): FormGroup {
    return this.form.get('jobDetails') as FormGroup;
  }

  createFollowUpGroup(): FormGroup {
    return this.fb.group({
      dateFollowUp: [{ value: null, disabled: true }],
      priorFirstDate: [{ value: null, disabled: true }],
      priorSecondDate: [{ value: null, disabled: true }],
      priorThirdDate: [{ value: null, disabled: true }],
      notes: [{ value: '', disabled: true }],
      ciw: [{ value: false, disabled: true }],
      cfm: [{ value: false, disabled: true }],
    });
  }

  get followUps(): FormArray {
    return (this.form.get('jobDetails') as FormGroup).get('followUps') as FormArray;
  }

  addFollowUp() {
    this.followUps.push(this.createFollowUpGroup());
  }

  removeFollowUp(index: number) {
    this.followUps.removeAt(index);
  }

  createShipmentGroup(): FormGroup {
    return this.fb.group({
      shippingMethod: [{ value: '', disabled: true }],
      trackingNumber: [{ value: '', disabled: true }],
      unitsAvailableToShip: [{ value: '', disabled: true }],
      units: [{ value: '', disabled: true }],
      totalPrice: [{ value: 0, disabled: true }],
      weight: [{ value: '', disabled: true }],
      dateShipped: [{ value: '', disabled: true }],
      quickbooksInvoice: [{ value: '', disabled: true }],
      shipmentPhoto: [{ value: '', disabled: true }],
      shippingNotes: [{ value: '', disabled: true }],
      dropShipment: [{ value: true, disabled: true }],
      dropShipAddress: [{ value: '', disabled: true }],
      dropStreetAddress2: [{ value: '', disabled: true }],
      dropCity: [{ value: '', disabled: true }],
      dropCountry: [{ value: '', disabled: true }],
      dropState: [{ value: '', disabled: true }],
      dropZip: [{ value: '', disabled: true }],
      dropAttn: [{ value: '', disabled: true }],
      dropShipBox: [{ value: '', disabled: true }],
      dropShipAttachment: [{ value: '', disabled: true }],
      packingSlip: [{ value: '', disabled: true }],
      attachments: this.fb.array([]),
    });
  }

  get shipments(): FormArray {
    return this.form.get('shipments') as FormArray;
  }

  onGeneralSave(order: any) {
    if (!this.orderId) return;
    const jobDetails = this.jobDetailsForm.value;

    // Format dates to YYYY-MM-DD using local time
    const formatDate = (date: Date | null) => {
      if (!date) return null;
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const shippingDetailsValue = this.form.get('shippingDetails')?.value;
    const shippingDetails = shippingDetailsValue ? [shippingDetailsValue] : [];

    const shipmentsValue = this.form.get('shipments')?.value || [];
    const shipments = shipmentsValue.map((shipment: any) => ({
      ...shipment,
      dateShipped: shipment.dateShipped ? formatDate(new Date(shipment.dateShipped)) : null,
      dropShipment: shipment.dropShipment ? 1 : 0,
      // Map attachments or other nested fields here if needed
    }));

    const orderToSave = {
      ...order,
      ...jobDetails,
      id: this.orderId,
      promisedDate: formatDate(jobDetails.promisedDate),
      expediteDate: formatDate(jobDetails.expediteDate),
      materialsReceived: this.form.get('materialsReceived')?.value || false,
      materials:
        this.form.get('materials')?.value?.map((material: any) => ({
          id: material.id || 0,
          category: material.category,
          description: material.description,
          quantity: material.quantity,
          poNo: material.poNo,
          expDate: material.expDate,
          recDate: formatDate(material.recDate),
          status: material.status,
          notes: material.notes,
          attachments:
            material.attachments?.map((attachment: any) => ({
              name: attachment.attachmentName,
              document: attachment.attachmentDocument,
            })) || [],
        })) || [],
      followUps: this.followUps.value.map((followUp: any) => ({
        id: followUp.id || 0,
        dateFollowUp: formatDate(followUp.dateFollowUp),
        priorFirstDate: formatDate(followUp.priorFirstDate),
        priorSecondDate: formatDate(followUp.priorSecondDate),
        priorThirdDate: formatDate(followUp.priorThirdDate),
        notes: followUp.notes || '',
        ciw: followUp.ciw ? 1 : 0,
        cfm: followUp.cfm ? 1 : 0,
      })),
      itemProcesses: this.itemProcessesComponent?.getItemProcessesPayload() || [],
      details: [
        {
          clientPartNo: this.form.get('specification')?.get('clientPartNo')?.value || '',
          awg: this.form.get('specification')?.get('awg')?.value || [],
          stranding: this.form.get('specification')?.get('stranding')?.value || [],
          wireType: this.form.get('specification')?.get('wireType')?.value || [],
          supplier: this.form.get('specification')?.get('supplier')?.value || [],
          finishedDiameter: this.form.get('specification')?.get('finishedDiameter')?.value || [],
          tip: this.form.get('specification')?.get('tip')?.value || [],
          die: this.form.get('specification')?.get('die')?.value || [],
          putUp: this.form.get('specification')?.get('putUp')?.value || [],
          jacketMaterial: this.form.get('specification')?.get('jacketMaterial')?.value || [],
          jacketColor: this.form.get('specification')?.get('jacketColor')?.value || [],
          jacketPrinting: this.form.get('specification')?.get('jacketPrinting')?.value || [],
          wallThickness: this.form.get('specification')?.get('wallThickness')?.value || [],
          innerDiameter: this.form.get('specification')?.get('innerDiameter')?.value || [],
          outerDiameter: this.form.get('specification')?.get('outerDiameter')?.value || [],
          description: this.form.get('specification')?.get('description')?.value || '',
        },
      ],
      shippingDetails,
      shipments,
    };

    this.store.dispatch(OrdersActions.saveOrder({ order: orderToSave, initiateRedirect: true }));
  }

  onGeneralCancel() {
    if (this.orderId) {
      this.router.navigate([`/orders/${this.orderId}/view`]);
    }
  }

  changeArchiveStatus(order: singleOrderDetails | null): void {
    if (!order) return;

    console.log(
      'Opening archive dialog for order:',
      order.id,
      'Current archive status:',
      order.archived,
    );
    const dialogRef = this.dialog.open(ArchiveOrderDialogComponent, {
      data: { order, isArchived: order.archived },
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log('Dialog closed with result:', result);
      if (result?.success) {
        const path = result.isArchived ? 'archived-orders' : 'active-orders';
        console.log('Navigating to:', ['/orders', path]);
        this.router.navigate(['/orders', path]).then((success: boolean): void => {
          if (success) {
            console.log('Navigation successful after archiving!');
          } else {
            console.warn('Navigation failed after archiving!');
          }
        });
      }
    });
  }

  deleteOrder(order: singleOrderDetails | null): void {
    if (!order) return;

    const dialogRef = this.dialog.open(DeleteOrderComponent, {
      data: { order },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        const path = order.archived ? 'archived-orders' : 'active-orders';
        this.router.navigate(['/orders', path]).then((success: boolean): void => {
          if (success) {
            console.log('Navigation successful after deletion!');
          } else {
            console.warn('Navigation failed after deletion!');
          }
        });
      }
    });
  }

  exportOrder(order: singleOrderDetails | null): void {
    if (!order) return;

    const dialogRef = this.dialog.open(OrderExportComponent, {
      data: { order },
      width: '800px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        console.log('Order export successful:', result);
      }
    });
  }
}
